import { EditOutlined } from '@ant-design/icons';
import { Button, Col, Form, message, Row, Space, Typography, Modal, Divider } from 'antd';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory, generatePath } from 'react-router-dom';
import styled from 'styled-components';
import { t, jt } from 'ttag';

import Loading from 'common/components/Loading';
import StickyFooter from 'common/components/StickyFooter';
import useConfig from 'common/hooks/useConfig';
import { getShortcode } from 'common/state/event';

import {
  CONF_SECTION_ORDER,
  CONF_VIRTUAL_EVENT,
  CONF_CAN_EXPIRE,
  CONF_NESTED_FIELDS,
} from './constants';
import EditCodeModal from './EditCodeModal';
import Field from './Field';
import {
  checkActivityFeedSetAsHomepage,
  checkLeadCaptureLicenseLimitExceeded,
} from './validations';
import VirtualEventModal from './VirtualEventModal';

const { Title, Text } = Typography;

const Head = styled.div`
  padding: 45px 0 35px 0;
  margin-left: 45px;

  & > h1 {
    margin: 0;
  }

  & > button {
    padding: 0;
  }
`;

const Wrap = styled.div`
  margin: 0 58px;
  padding-bottom: 65px;

  .ant-form-item {
    margin-bottom: 10px;
    width: 300px;
  }
`;

const Section = ({ section, config }) => (
  <div>
    <Title level={4}>{section.name}</Title>
    {section.data
      .sort((a, b) => config[a].name.localeCompare(config[b].name))
      .map(key => (
        <Field key={key} config={config} configKey={key} />
      ))}
  </div>
);

const useConfiguration = () => {
  const { keys, get, find, loading, set: updateConfig } = useConfig();
  const config = keys.reduce((acc, keyValue) => {
    const { key } = keyValue;

    const entry = find(key);
    acc[key] = {
      ...keyValue,
      // remvove displayName when ff new_config_uapi is removed
      name: keyValue.name ?? keyValue.displayName,
      value: get(key),
      defaultValue: keyValue.default,
      subConfigKeys: CONF_NESTED_FIELDS[key],
      canExpire: CONF_CAN_EXPIRE.includes(key),
      expiry: entry && entry.expiresAt ? moment(entry.expiresAt) : null,
    };

    return acc;
  }, {});

  return { config, loading, updateConfig };
};

const Configuration = () => {
  const { organizationId, eventId } = useParams();
  const shortcode = useSelector(getShortcode);
  const { config, loading, updateConfig } = useConfiguration();

  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [showVirtualEventModal, setShowVirtualEventModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [touched, setTouched] = useState(false);
  const [form] = Form.useForm();

  const handleVirtualEventModal = () => {
    setShowVirtualEventModal(false);
    const fieldsValues = form.getFieldsValue(CONF_VIRTUAL_EVENT);
    CONF_VIRTUAL_EVENT.forEach(field => {
      form.setFieldValue(field, { ...fieldsValues[field], value: true });
    });
    setTouched(true);
  };

  const validate = async payload => {
    const errors = [];

    const [isLeadCaptureLimitExceeding, isActivityFeedSetAsHomepage] = await Promise.all([
      // Lead Capture License Limit should not be less than the current number of companies with the licenses turned on
      checkLeadCaptureLicenseLimitExceeded(eventId, payload),

      // Activity Feed should not be allowed to be turned off if it is set as the Homepage
      checkActivityFeedSetAsHomepage(eventId, payload),
    ]);

    if (isLeadCaptureLimitExceeding) {
      const leadCaptureCompaniesLink = (
        <Typography.Link
          onClick={() => {
            Modal.destroyAll();
            history.replace(generatePath('onsite/lead-capture'));
          }}
        >
          {t`companies with Lead Capture License`}
        </Typography.Link>
      );

      errors.push(
        jt`The limit of Lead Capture Licenses cannot be less than the current number of companies with the licenses turned on. Please check current number of ${leadCaptureCompaniesLink} before you lower the limit.`,
      );
    }

    if (isActivityFeedSetAsHomepage) {
      errors.push(t`The Activity Feed is currently set as the Homepage.`);
    }

    return errors;
  };

  const onFinish = async payload => {
    try {
      setUpdating(true);

      const validationErrors = await validate(payload);
      if (validationErrors.length > 0) {
        Modal.warning({
          title: t`Unable to save changes`,
          content: (
            <>
              <p>{t`Your changes cannot be saved for the following reason(s)`}:</p>

              <ul>
                {validationErrors.map(error => (
                  <li key={error}>
                    <p>{error}</p>
                  </li>
                ))}
              </ul>
            </>
          ),
          okText: t`Ok`,
        });
        return;
      }
      const configs = Object.entries(payload).map(([key, { value, expiry }]) => ({
        key,
        value,
        expiresAt: expiry ? expiry.utc().format('YYYY-MM-DD') : undefined,
      }));

      await updateConfig(configs, true);

      setTouched(false);

      message.success(t`Successfully updated feature configuration.`);
    } catch (err) {
      const fluxError = err.errors?.[0];
      if (fluxError?.code === 'dependency_config_not_enabled') {
        const { config: childKey, dependency_config: parentKey } = fluxError.params;

        message.error(
          t`Parent config '${config[parentKey].name}' cannot be changed in the same operation as its child '${config[childKey].name}'. Change the parent separately.`,
          // Increasing the duration so there's time to read it
          5,
        );
      } else {
        message.error(t`Unable to update feature configuration.`);
        throw err;
      }
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      {showModal && (
        <EditCodeModal
          organizationId={organizationId}
          eventId={eventId}
          eventCode={shortcode}
          onClose={() => setShowModal(false)}
        />
      )}
      {showVirtualEventModal && (
        <VirtualEventModal
          config={config}
          onCancel={() => setShowVirtualEventModal(false)}
          onOk={handleVirtualEventModal}
        />
      )}
      <Head>
        <Title level={1}>{t`Event Configuration`}</Title>
        <Button type="link" onClick={() => setShowModal(true)}>
          <EditOutlined /> {t`Edit Event Code`}
        </Button>
      </Head>

      <Wrap>
        {loading && <Loading />}

        {!loading && (
          <Form
            hideRequiredMark
            form={form}
            colon={false}
            layout="vertical"
            initialValues={config}
            size="small"
            onFinish={onFinish}
            onFieldsChange={() => setTouched(true)}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space direction="vertical">
                <div>
                  <Title level={4}>{t`Quick Actions`} </Title>
                  <Text type="secondary">
                    {t`Use quick configs to easily turn on all related features.`}
                  </Text>
                </div>
                <Button onClick={() => setShowVirtualEventModal(true)}>
                  {t`Turn on Virtual Event Configs`}
                </Button>
              </Space>
              <Divider />
            </Space>
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
              <Row>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }} size={20}>
                    {CONF_SECTION_ORDER.left.map(section => (
                      <Section key={section.name} section={section} config={config} />
                    ))}
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }} size={20}>
                    {CONF_SECTION_ORDER.right.map(section => (
                      <Section key={section.name} section={section} config={config} />
                    ))}
                  </Space>
                </Col>
              </Row>
            </Space>
            <StickyFooter>
              <Button
                type="primary"
                htmlType="submit"
                size="default"
                loading={updating}
                disabled={loading || !touched}
              >
                {t`Save Configuration`}
              </Button>
            </StickyFooter>
          </Form>
        )}
      </Wrap>
    </>
  );
};

export default Configuration;
